<template>
  <div :class="classObject">
    <input
      type="checkbox"
      :name="name"
      :id="id"
      :checked="checked"
      :value="value"
      @change="emit($event)"
    />
    <label class="checkboxButton_label" :for="id" v-html="label"></label>
  </div>
</template>

<script lang="ts">
import { defineComponent, SetupContext, computed } from 'vue'
import { Checkbox } from '@/types/InputItems'

export default defineComponent({
  name: 'InputCheckboxButton',

  props: {
    id: {
      type: String,
      required: true
    },
    label: {
      type: String,
      required: true
    },
    value: {
      type: String,
      required: true
    },
    name: {
      type: String
    },
    inline: {
      type: Boolean,
      default: false
    },
    block: {
      type: Boolean,
      default: false
    },
    narrow: {
      type: Boolean,
      default: false
    },
    error: {
      type: Boolean,
      default: false
    },
    checked: {
      type: Boolean,
      default: false
    }
  },

  setup(props: Checkbox, context: SetupContext) {
    const classObject = computed(() => {
      return {
        checkboxButton: true,
        inline: props.inline,
        block: props.block,
        narrow: props.narrow,
        error: props.error
      }
    })

    const emit = (e: any) => {
      context.emit('call-input-change', {
        value: e.target.value,
        checked: e.target.checked
      })
    }

    return {
      emit,
      classObject
    }
  }
})
</script>

<style lang="scss" scoped>
@use 'src/styles/abstracts/mixins';
@use 'src/styles/abstracts/variables' as var;

.checkboxButton {
  & + & {
    margin-top: 20px;
  }
}

.checkboxButton > input[type='checkbox'] {
  display: none;
}

.checkboxButton_label {
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  height: 60px;
  padding: 10px 20px 10px 12px;
  border: 1px solid var.$color-border-gray;
  border-radius: 10px;
  box-shadow: 0 6px 9px rgba(0, 0, 0, 0.08);
  line-height: 1.3;
  font-size: 1.3rem;
  font-weight: 900;
  transition: background 0.1s ease, border 0.1s ease;

  @include mixins.media() {
    line-height: 1.4;
    font-size: 1.4rem;
  }

  &::before {
    display: inline-block;
    content: '';
    width: 20px;
    height: 20px;
    margin-right: 12px;
    background-color: var.$color-border-gray;
    border-radius: 4px;
    flex-shrink: 0;
    transition: background 0.1s ease;
  }

  &::after {
    opacity: 0;
    position: absolute;
    top: 0;
    left: 2px;
    display: inline-block;
    content: '';
    width: 12px;
    height: 8px;
    border-left: 2px solid #fff;
    border-bottom: 2px solid #fff;
    transform: rotate(-45deg) translate(-6px, 26px);
    transition: opacity 0.1s ease;
  }

  &:hover {
    background-color: #e5f7f6;
  }

  .checkboxButton > input[type='checkbox']:checked + & {
    border-color: var.$color-secondary;
    background-color: var.$color-secondary;
    color: #30bab2;

    &::before {
      background-color: var.$color-main;
    }

    &::after {
      opacity: 1;
    }
  }
}

.inline .checkboxButton_label {
  margin-top: 20px;
  height: auto;
  padding: 0;
  box-shadow: none;
  border: none;
  font-weight: normal;

  &::before {
    background-color: #fff;
    border: 1px solid #ddd;
  }
}

.checkboxButton.inline
  > input[type='checkbox']:checked
  + .checkboxButton_label {
  border-color: var.$color-secondary;
  background-color: transparent;
  color: var.$color-black;

  &::after {
    top: -18px;
    left: -10px;
  }
}

.checkboxButton.block {
  @include mixins.media() {
    max-width: 336px;
    margin: 0 auto;
  }
}

.narrow .checkboxButton_label {
  height: 50px;
  font-weight: 500;

  &::after {
    transform: rotate(-45deg) translate(-3px, 23px);
  }
}

.checkboxButton.narrow
  > input[type='checkbox']:checked
  + .checkboxButton_label {
  border-color: var.$color-main;
  background-color: #fff;
  color: var.$color-black;
}

.error .checkboxButton_label {
  border-color: var.$color-red;
}
</style>
